import { useEffect, useMemo, useState } from "react";
import { SafeImage } from "@/components/site/SafeImage";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const CATEGORIES = ["All", "Entrepreneurship", "Startups", "Business", "Technology", "AI", "Marketing", "Finance", "Branding", "E-commerce", "India Growth"];

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Blog | LaunchLab";
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  const filtered = useMemo(() => {
    if (!posts) return [];
    return posts.filter((p) => {
      const matchCat = category === "All" || p.category === category;
      const q = query.trim().toLowerCase();
      const matchQ = !q || [p.title, p.excerpt, ...(p.tags || [])].join(" ").toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [posts, query, category]);

  return (
    <div data-testid="blog-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="blog-hero"
        kicker="LaunchLab Insights"
        title="IDEAS, STRATEGY &"
        highlight="EXECUTION."
        subtext="Perspectives on entrepreneurship, startups, technology and building businesses in India."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5 mb-10">
          <div className="relative lg:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="blog-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles..."
              className="w-full glass rounded-full pl-10 pr-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-[#FF8800]"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="blog-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors ${
                  category === c ? "bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E]" : "glass text-slate-300 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {posts === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="blog-empty">No articles found.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block rounded-2xl border border-slate-800 bg-[#0D111D]/90 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF8800]/40"
                >
                  <div className="relative w-full aspect-[1200/630] overflow-hidden bg-grid-tech">
                    <SafeImage
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-display font-extrabold text-4xl text-white/5 tracking-widest">LAUNCHLAB</span>
                        </div>
                      }
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.16em] text-[#00B0FF] uppercase">
                      {p.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-white leading-snug group-hover:text-[#FF8800] transition-colors">{p.title}</h3>
                    <p className="mt-2.5 text-sm text-slate-400 leading-relaxed line-clamp-2">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-slate-500">{p.author} · {p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                      <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#FF8800] transition-colors" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
